import { perimeter, polygonArea, type Vec2 } from '@indusketch/geometry';
import { columnSpan } from '../columns.js';
import { hallRoofLoad } from '../loads.js';
import { mezzanineArea, mezzanineColumns } from '../mezzanine.js';
import { officeLevelSlab, officeSlabLevels } from '../office.js';
import type { Building, Column, ProjectDocument, Wall } from '../types.js';
import { GIRDER_SOLIDITY, hallSteel, type HallSteel } from './hallStructure.js';
import { officeStructure } from './officeStructure.js';
import { wallSolidParts } from './walls.js';

export type ConcretePart = 'slab' | 'beam' | 'column' | 'wall' | 'foundation';

export const CONCRETE_PARTS: readonly ConcretePart[] = [
  'slab',
  'beam',
  'column',
  'wall',
  'foundation',
];

/** Usual reinforcement per cubic metre of concrete (kg/m³) for a rough quantity. */
export const REBAR_RATE: Readonly<Record<ConcretePart, number>> = {
  slab: 90,
  beam: 130,
  column: 200,
  wall: 60,
  foundation: 50,
};

/** Concrete volume (m³), reinforcement (t) and formwork area (m²). */
export interface ConcreteAmount {
  readonly volume: number;
  readonly rebar: number;
  readonly formwork: number;
}

export interface ConcreteQuantities {
  readonly parts: Readonly<Record<ConcretePart, ConcreteAmount>>;
  readonly volume: number;
  readonly rebar: number;
  readonly formwork: number;
}

/** Pad footing under a column: square, `side` wide and `depth` thick, for `load` kN. */
export interface Footing {
  readonly at: Vec2;
  readonly load: number;
  readonly side: number;
  readonly depth: number;
}

/** Allowable ground pressure (kN/m², characteristic loads) of an ordinary bearing soil. */
export const SOIL_PRESSURE = 250;
/** Smallest pad footing and the frost-free depth of footings along the facade. */
const MIN_PAD = 0.8;
const FROST_DEPTH = 0.8;
/** Smallest socket footing of a fixed concrete column. */
const SOCKET_MIN = 1.6;
/** Own weight of precast binders and beams spread over the roof (kN/m²). */
const PRECAST_ROOF_WEIGHT = 1;
/** Load of a mezzanine (storage 5 kN/m² and about 2.5 kN/m² own weight). */
const MEZZANINE_LOAD = 7.5;
/** A storey of an office on its columns: slab, finishes, partitions and use (kN/m²). */
const OFFICE_STOREY_LOAD = 10;
/** Floor area a column set by hand is taken to carry. */
const HAND_COLUMN_AREA = 25;
/** Facade (panels, girts) hanging on an edge column (kN/m²). */
const FACADE_LOAD = 0.3;
/** Frost apron under a hall facade, strip under a load-bearing wall (width, depth). */
const APRON = { width: 0.25, depth: FROST_DEPTH };
const WALL_STRIP = { width: 0.5, depth: FROST_DEPTH };
/** Massive walls inside an office stand on a strip within the ground slab. */
const INNER_STRIP = { width: 0.4, depth: 0.5 };
const MASSIVE: readonly Wall['cladding'][] = ['sand-lime', 'concrete', 'brick'];

const roundUp = (v: number, step: number) => Math.ceil(v / step - 1e-9) * step;

const skeleton = (b: Building): boolean =>
  b.office?.structure !== undefined && b.office.structure.system !== 'walls';

/**
 * Wall cast in concrete: concrete walls, and the stairwells of an office skeleton, which as
 * concrete cores brace it.
 */
export function concreteWall(doc: Pick<ProjectDocument, 'buildings' | 'stairs'>, w: Wall): boolean {
  if (w.cladding === 'concrete') return true;
  const stair = w.ownerId ? doc.stairs[w.ownerId] : undefined;
  const office = stair ? doc.buildings[stair.buildingId] : undefined;
  return office !== undefined && skeleton(office) && stair?.enclosed === true;
}

/**
 * Footing for a characteristic column load, frost-free where it stands along the facade. A
 * concrete column fixed in a socket (`socket` = its size) needs a deeper and wider one.
 */
export function padFooting(at: Vec2, load: number, outside: boolean, socket?: number): Footing {
  let side = roundUp(Math.max(MIN_PAD, Math.sqrt((1.1 * load) / SOIL_PRESSURE)), 0.1);
  let depth = Math.max(outside ? FROST_DEPTH : 0.5, roundUp(0.35 * side, 0.1));
  if (socket !== undefined) {
    side = Math.max(side, SOCKET_MIN, roundUp(3 * socket, 0.1));
    depth = Math.max(depth, 1, roundUp(1.4 * socket + 0.4, 0.1));
  }
  return { at, load, side, depth };
}

/** Column loads of a hall's frame from the roof area each column carries. */
function frameFootings(b: Building, steel: HallSteel): Footing[] {
  const last = String(steel.bays + 1);
  const end = (axis?: string) => (axis === '1' || axis === last ? 0.5 : 1);
  const load = steel.roofLoad + (steel.system === 'precast' ? PRECAST_ROOF_WEIGHT : 0);
  return steel.columns.map((c) => {
    const roof =
      c.kind === 'column'
        ? end(c.axis) * steel.bayLength * (steel.rafterSpan / 2)
        : c.kind === 'interiorColumn'
          ? end(c.axis) * steel.bayLength * steel.interiorSpacing * steel.rafterSpan
          : (steel.bayLength * steel.bayLength) / 2;
    const facade = c.kind === 'interiorColumn' ? 0 : FACADE_LOAD * b.eaveHeight * steel.bayLength;
    const socket = c.profile === null ? Math.max(c.obb.width, c.obb.depth) : undefined;
    return padFooting(c.center, roof * load + facade, c.kind !== 'interiorColumn', socket);
  });
}

/** Precast columns, beams and binders of the halls (m³), delivered with their reinforcement. */
export function precastVolume(doc: ProjectDocument): number {
  let sum = 0;
  for (const b of Object.values(doc.buildings)) {
    const frame = hallSteel(doc, b);
    if (!frame) continue;
    for (const m of frame.bars)
      sum += m.width * m.depth * Math.hypot(m.b.x - m.a.x, m.b.y - m.a.y, m.b.z - m.a.z);
    for (const g of frame.girders)
      g.top.forEach((t, i) => {
        const [b0, b1, t1] = [g.bottom[i], g.bottom[i + 1], g.top[i + 1]];
        if (b0 && b1 && t1) sum += sideArea([b0, b1, t1, t]) * g.thickness * GIRDER_SOLIDITY;
      });
  }
  return sum;
}

/** Area of a flat polygon in space (Newell's method). */
function sideArea(corners: readonly { x: number; y: number; z: number }[]): number {
  let nx = 0;
  let ny = 0;
  let nz = 0;
  corners.forEach((a, i) => {
    const c = corners[(i + 1) % corners.length] ?? a;
    nx += (a.y - c.y) * (a.z + c.z);
    ny += (a.z - c.z) * (a.x + c.x);
    nz += (a.x - c.x) * (a.y + c.y);
  });
  return Math.hypot(nx, ny, nz) / 2;
}

/** Load on a column set by hand standing on the ground. */
function handColumnLoad(doc: ProjectDocument, c: Column, roofLoad: number): number {
  const b = doc.buildings[c.buildingId];
  if (!b) return 0;
  if (b.office) {
    const above = b.office.floors.length - c.level;
    return HAND_COLUMN_AREA * OFFICE_STOREY_LOAD * Math.max(1, above);
  }
  const m = c.mezzanineId ? doc.mezzanines[c.mezzanineId] : undefined;
  if (m) {
    const n = Object.values(doc.columns).filter((x) => x.mezzanineId === m.id).length;
    return (mezzanineArea(m) / Math.max(1, n)) * MEZZANINE_LOAD;
  }
  return HAND_COLUMN_AREA * roofLoad;
}

/** Pad footings under every column standing on the ground. */
export function columnFootings(doc: ProjectDocument): Footing[] {
  const out: Footing[] = [];
  for (const b of Object.values(doc.buildings)) {
    const steel = hallSteel(doc, b);
    if (steel) out.push(...frameFootings(b, steel));
    const outdoor = b.office?.placement === 'outdoor';
    for (const c of officeStructure(doc, b)?.columns ?? [])
      if (c.level === 0) out.push(padFooting(c.center, c.load, c.edge && outdoor));
  }
  const roofLoad = hallRoofLoad(doc.settings);
  for (const c of Object.values(doc.columns)) {
    if (columnSpan(doc, c).base > 1e-6) continue;
    out.push(padFooting(c.center, handColumnLoad(doc, c, roofLoad), false));
  }
  for (const m of Object.values(doc.mezzanines)) {
    const at = mezzanineColumns(doc, m);
    const share = (mezzanineArea(m) / Math.max(1, at.length)) * MEZZANINE_LOAD;
    for (const p of at) out.push(padFooting(p, share, false));
  }
  return out;
}

/** Strips under the walls standing on the ground: hall facades, office walls. */
function stripFoundations(
  doc: ProjectDocument,
): { length: number; width: number; depth: number }[] {
  const out: { length: number; width: number; depth: number }[] = [];
  for (const w of Object.values(doc.walls)) {
    if (w.level !== 0 || w.baseElevation > 1e-6) continue;
    const owner = w.ownerId ? doc.buildings[w.ownerId] : undefined;
    const length = Math.hypot(w.end.x - w.start.x, w.end.y - w.start.y);
    if (owner && w.kind === 'exterior') {
      // A skeleton carries its slabs on columns: its facade only needs a frost apron.
      const strip = !owner.office
        ? APRON
        : skeleton(owner)
          ? owner.office.placement === 'indoor'
            ? null
            : APRON
          : owner.office.placement === 'indoor'
            ? INNER_STRIP
            : WALL_STRIP;
      if (strip) out.push({ length, ...strip });
    } else if (MASSIVE.includes(w.cladding) || concreteWall(doc, w))
      out.push({ length, ...INNER_STRIP });
  }
  return out;
}

type Amounts = Record<ConcretePart, { volume: number; formwork: number }>;

/** Office floor slabs: area × thickness, formwork under the slab and along its edge. */
function addSlabs(doc: ProjectDocument, add: (part: ConcretePart, v: number, f: number) => void) {
  for (const b of Object.values(doc.buildings)) {
    if (!b.office) continue;
    for (const level of officeSlabLevels(b.office)) {
      const outline = officeLevelSlab(b, level);
      const d = b.office.slabThickness;
      const area = polygonArea(outline);
      add('slab', area * d, area + perimeter(outline) * d);
    }
  }
}

const quantitiesCache = new WeakMap<ProjectDocument, ConcreteQuantities>();

/**
 * Reinforced concrete of the project: office slabs, concrete columns and walls and, if the
 * cost settings ask for it, the footings under all columns and walls on the ground.
 * Reinforcement from usual rates per part; a quantity for an early budget, not a design.
 */
export function concreteQuantities(doc: ProjectDocument): ConcreteQuantities {
  const cached = quantitiesCache.get(doc);
  if (cached) return cached;
  const sum: Amounts = {
    slab: { volume: 0, formwork: 0 },
    beam: { volume: 0, formwork: 0 },
    column: { volume: 0, formwork: 0 },
    wall: { volume: 0, formwork: 0 },
    foundation: { volume: 0, formwork: 0 },
  };
  const add = (part: ConcretePart, volume: number, formwork: number) => {
    sum[part].volume += volume;
    sum[part].formwork += formwork;
  };

  addSlabs(doc, add);
  for (const c of Object.values(doc.columns)) {
    if (c.material !== 'concrete') continue;
    const span = columnSpan(doc, c);
    const h = span.top - span.base;
    add('column', c.width * c.depth * h, 2 * (c.width + c.depth) * h);
  }
  for (const b of Object.values(doc.buildings)) {
    const s = officeStructure(doc, b);
    if (!s) continue;
    for (const c of s.columns) {
      const h = c.top - c.base;
      add('column', c.size * c.size * h, 4 * c.size * h);
    }
    for (const m of s.beams) {
      const length = Math.hypot(m.b.x - m.a.x, m.b.y - m.a.y);
      add('beam', m.width * m.drop * length, (2 * m.drop + m.width) * length);
    }
  }
  for (const w of Object.values(doc.walls)) {
    if (!concreteWall(doc, w)) continue;
    const area = wallSolidParts(doc, w).reduce(
      (s, p) => s + (p.to - p.from) * (p.top - p.bottom),
      0,
    );
    add('wall', area * w.thickness, 2 * area);
  }
  if (doc.settings.costs?.foundationsFromColumns) {
    for (const f of columnFootings(doc))
      add('foundation', f.side ** 2 * f.depth, 4 * f.side * f.depth);
    for (const s of stripFoundations(doc))
      add('foundation', s.length * s.width * s.depth, 2 * s.length * s.depth);
  }

  const parts = Object.fromEntries(
    CONCRETE_PARTS.map((p) => [p, { ...sum[p], rebar: (sum[p].volume * REBAR_RATE[p]) / 1000 }]),
  ) as Record<ConcretePart, ConcreteAmount>;
  const total = (key: keyof ConcreteAmount) =>
    CONCRETE_PARTS.reduce((s, p) => s + parts[p][key], 0);
  const result = {
    parts,
    volume: total('volume'),
    rebar: total('rebar'),
    formwork: total('formwork'),
  };
  quantitiesCache.set(doc, result);
  return result;
}
